import { useEffect, useState } from "react"
import "./InstallGate.css"

const GUIDE = {
  IOS: "ios",
  CHROME_ANDROID: "chrome-android",
  SAMSUNG: "samsung",
  FIREFOX_ANDROID: "firefox-android",
  OTHER: "other"
}

// Launched from the installed icon (navigator.standalone on iOS); a full-screen browser window is not.
function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    window.navigator.standalone === true
  )
}

function isFullscreen() {
  return window.matchMedia?.("(display-mode: fullscreen)").matches === true
}

// iPadOS 13+ reports itself as MacIntel, so touch points are the giveaway.
function isIOS(ua) {
  return (
    /iphone|ipad|ipod/i.test(ua) ||
    (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1)
  )
}

// Premium Android tablets get Chrome's desktop site, which reports Linux; touch gives them away.
function isAndroid(ua) {
  return (
    /android/i.test(ua) ||
    (/linux/i.test(ua) && !/cros/i.test(ua) && window.navigator.maxTouchPoints > 0)
  )
}

function hasBrand(pattern) {
  return window.navigator.userAgentData?.brands?.some((entry) => pattern.test(entry.brand)) === true
}

// Picks the install steps for this browser; the steps differ by where its menu is and what it calls Install.
function getInstallGuide() {
  const ua = window.navigator.userAgent
  if (isIOS(ua)) return GUIDE.IOS
  if (!isAndroid(ua)) return GUIDE.OTHER
  if (hasBrand(/samsung/i) || /samsungbrowser/i.test(ua)) return GUIDE.SAMSUNG
  if (/firefox/i.test(ua)) return GUIDE.FIREFOX_ANDROID
  if (hasBrand(/google chrome/i) || (/chrome\//i.test(ua) && !/edga?\/|opr\//i.test(ua))) {
    return GUIDE.CHROME_ANDROID
  }
  return GUIDE.OTHER
}

export default function InstallGate({ children }) {
  const [isInstalled, setIsInstalled] = useState(isStandalone)
  // Installed from this tab, which on Android stays a browser tab until the icon is opened.
  const [hasInstalled, setHasInstalled] = useState(false)
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [guide] = useState(getInstallGuide)

  useEffect(() => {
    // Chrome/Edge hand us the prompt; hold it so the button can fire it.
    const onBeforeInstall = (event) => {
      event.preventDefault()
      setDeferredPrompt(event)
    }
    window.addEventListener("beforeinstallprompt", onBeforeInstall)

    const onInstalled = () => {
      setHasInstalled(true)
      setDeferredPrompt(null)
    }
    window.addEventListener("appinstalled", onInstalled)

    // Unlocks when desktop Chrome moves the tab into the app window; stays unlocked if that window goes full screen.
    const standalone = window.matchMedia?.("(display-mode: standalone)")
    const onModeChange = () =>
      setIsInstalled((wasInstalled) => isStandalone() || (wasInstalled && isFullscreen()))
    standalone?.addEventListener?.("change", onModeChange)

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall)
      window.removeEventListener("appinstalled", onInstalled)
      standalone?.removeEventListener?.("change", onModeChange)
    }
  }, [])

  if (isInstalled) return children

  if (hasInstalled) {
    return (
      <div className="install-gate">
        <div className="install-gate__inner">
          <img className="install-gate__mark" src="/img/vantero-wordmark.svg" alt="Vantero Hospitality" />
          <h1 className="install-gate__title">Installed</h1>
          <p className="install-gate__body">
            Vantero Menu is now on this device. Close the browser and open it from its icon on
            your home screen.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="install-gate">
      <div className="install-gate__inner">
        <img className="install-gate__mark" src="/img/vantero-wordmark.svg" alt="Vantero Hospitality" />

        <h1 className="install-gate__title">Install to continue</h1>
        <p className="install-gate__body">
          Vantero Menu runs as an app on this device, not in the browser. Add it to your
          home screen to get started.
        </p>

        {deferredPrompt ? (
          <button
            type="button"
            className="install-gate__button"
            onClick={async () => {
              deferredPrompt.prompt()
              const { outcome } = await deferredPrompt.userChoice
              if (outcome === "accepted") setHasInstalled(true)
              setDeferredPrompt(null)
            }}
          >
            Install Vantero Menu
          </button>
        ) : (
          <InstallSteps guide={guide} />
        )}

        <p className="install-gate__note">Already installed? Open Vantero Menu from its icon.</p>
      </div>
    </div>
  )
}

function InstallSteps({ guide }) {
  if (guide === GUIDE.IOS) {
    return (
      <ol className="install-gate__steps">
        <li>
          Tap <span className="install-gate__share" aria-hidden="true" /> Share in the Safari
          toolbar
        </li>
        <li>Choose Add to Home Screen</li>
        <li>Open Vantero Menu from the new icon</li>
      </ol>
    )
  }

  if (guide === GUIDE.CHROME_ANDROID) {
    return (
      <ol className="install-gate__steps">
        <li>
          Tap <span className="install-gate__more" role="img" aria-label="Menu" /> at the top
          right of Chrome
        </li>
        <li>Choose Add to home screen, or Install app</li>
        <li>Pick Install, not Create shortcut</li>
        <li>Open Vantero Menu from the new icon</li>
      </ol>
    )
  }

  if (guide === GUIDE.SAMSUNG) {
    return (
      <ol className="install-gate__steps">
        <li>
          Tap <span className="install-gate__lines" role="img" aria-label="Menu" /> in the
          Samsung Internet toolbar
        </li>
        <li>Choose Add page to, then Home screen</li>
        <li>Open Vantero Menu from the new icon</li>
      </ol>
    )
  }

  if (guide === GUIDE.FIREFOX_ANDROID) {
    return (
      <ol className="install-gate__steps">
        <li>
          Tap <span className="install-gate__more" role="img" aria-label="Menu" /> in the
          Firefox toolbar
        </li>
        <li>Choose Install, then Add automatically</li>
        <li>Open Vantero Menu from the new icon</li>
      </ol>
    )
  }

  return (
    <ol className="install-gate__steps">
      <li>Open your browser menu</li>
      <li>Choose Install app, or Add to Home screen</li>
      <li>Open Vantero Menu from the new icon</li>
    </ol>
  )
}
